import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, FileSpreadsheet, Download, CreditCard, Gift, Loader2 } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

import { API } from "./apiConfig";

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#0A1F3B]">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
      {sub && <p className="text-[10px] text-slate-400 mt-2">{sub}</p>}
    </div>
  );
}

export function AdminDashboard({ isOpen, onClose }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCredits, setEditingCredits] = useState({});
  const [savingEmail, setSavingEmail] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
      ]);
      if (statsRes.status === "fulfilled") {
        setStats(statsRes.value.data);
      } else {
        const errDetail = statsRes.reason?.response?.data?.detail || statsRes.reason?.message || "Unknown error";
        const errStatus = statsRes.reason?.response?.status || "network";
        console.error("Admin stats error:", errStatus, errDetail);
        toast.error(`Admin stats failed (${errStatus})`, { description: errDetail });
      }
      if (usersRes.status === "fulfilled") {
        setUsers(usersRes.value.data.users || []);
      } else {
        const errDetail = usersRes.reason?.response?.data?.detail || usersRes.reason?.message || "Unknown error";
        const errStatus = usersRes.reason?.response?.status || "network";
        console.error("Admin users error:", errStatus, errDetail);
        toast.error(`User list failed (${errStatus})`, { description: errDetail });
      }
    } catch (err) {
      toast.error("Failed to load admin data", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  const saveCredits = async (email) => {
    const credits = parseInt(editingCredits[email]) || 0;
    setSavingEmail(email);
    try {
      await axios.post(`${API}/admin/user-credits`, { email, export_credits: credits }, { withCredentials: true });
      toast.success(`${email} now has ${credits} credits`);
      setUsers(prev => prev.map(u => u.email === email ? { ...u, export_credits: credits } : u));
      setEditingCredits(prev => { const n = { ...prev }; delete n[email]; return n; });
    } catch {
      toast.error("Failed to update credits");
    } finally {
      setSavingEmail(null);
    }
  };

  if (!isOpen) return null;

  const planBadge = (u) => {
    if (u.role === "admin") return { text: "Admin", cls: "text-violet-700 bg-violet-50" };
    if (u.subscription_status === "unlimited" || u.subscription_status === "paid") return { text: "Paid", cls: "text-emerald-700 bg-emerald-50" };
    return { text: "Free", cls: "text-slate-600 bg-slate-100" };
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-dashboard">
      <div className="bg-slate-50 rounded-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="bg-[#0A1F3B] px-6 py-4 flex items-center justify-between">
          <h2 className="text-white font-semibold text-base">Admin Dashboard</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white text-lg" data-testid="admin-close">X</button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-[#0A1F3B]" size={28} />
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={Users} label="Total Users" value={stats?.total_users || 0} sub={`${stats?.free_users || 0} free / ${stats?.paid_users || 0} paid`} color="bg-[#0A1F3B]" />
                <StatCard icon={FileSpreadsheet} label="Reports Generated" value={stats?.total_reports || 0} color="bg-blue-600" />
                <StatCard icon={Download} label="Total Downloads" value={stats?.total_downloads || 0} color="bg-emerald-600" />
                <StatCard icon={CreditCard} label="Transactions" value={stats?.total_transactions || 0} color="bg-amber-600" />
              </div>

              {/* Users Table */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="users-table">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#0A1F3B]">Site Users ({users.length})</h3>
                  <p className="text-[10px] text-slate-400">Paywall: 1st free, 2nd login, 3rd payment (unless credits granted)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
                        <th className="text-left px-4 py-2 font-medium">Email</th>
                        <th className="text-left px-4 py-2 font-medium">Plan</th>
                        <th className="text-right px-4 py-2 font-medium">Exports Used</th>
                        <th className="text-center px-4 py-2 font-medium">
                          <span className="flex items-center justify-center gap-1"><Gift size={12} /> Free Credits</span>
                        </th>
                        <th className="text-left px-4 py-2 font-medium">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => {
                        const badge = planBadge(u);
                        const isEditing = editingCredits.hasOwnProperty(u.email);
                        return (
                          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`user-row-${i}`}>
                            <td className="px-4 py-2.5 text-slate-800 font-mono">{u.email}</td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${badge.cls}`}>{badge.text}</span>
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-700 font-mono">{u.exports_used}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center justify-center gap-1.5">
                                {isEditing ? (
                                  <>
                                    <Input
                                      type="number"
                                      min={0}
                                      value={editingCredits[u.email]}
                                      onChange={(e) => setEditingCredits(prev => ({ ...prev, [u.email]: e.target.value }))}
                                      className="w-16 h-6 text-xs text-center text-[#0A1F3B] font-semibold"
                                      data-testid={`credits-input-${i}`}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => saveCredits(u.email)}
                                      disabled={savingEmail === u.email}
                                      className="bg-[#0A1F3B] text-white h-6 px-2 text-[10px]"
                                      data-testid={`credits-save-${i}`}
                                    >
                                      {savingEmail === u.email ? <Loader2 size={10} className="animate-spin" /> : "Save"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingCredits(prev => { const n = { ...prev }; delete n[u.email]; return n; })}
                                      className="h-6 px-1.5 text-[10px] text-slate-500"
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <span className={`font-mono ${u.export_credits > 0 ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                                      {u.export_credits}
                                    </span>
                                    <button
                                      onClick={() => setEditingCredits(prev => ({ ...prev, [u.email]: u.export_credits }))}
                                      className="text-[10px] text-blue-600 hover:text-blue-800 underline ml-1"
                                      data-testid={`credits-edit-${i}`}
                                    >
                                      edit
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
